/**
 * 数据容器 — 集中导入所有数据表，提供统一访问接口
 */
import { ATTRIBUTE_DEFS, ATTRIBUTE_KEYS } from './tables/attributes';
import { STAGES } from './tables/Stages';
import { SCHEDULES, SCHEDULE_MAP } from './tables/Schedules';
import { SCRIPTS, SCRIPT_MAP } from './tables/Scripts';
import { BRANDS, BRAND_MAP } from './tables/Brands';
import { WS_MOCK_LINES, ANNOUNCE_COPY, ANNOUNCE_COMMENTS } from './tables/EndorsementCopy';
import { SPECIAL_SCRIPTS } from './tables/SpecialScripts';
import { FIXED_EVENTS } from './tables/FixedEvents';
import { TIMELINE_EVENTS } from './tables/TimelineEvents';
import { SPECIAL_NPCS, roleAnchorOf } from './tables/SpecialNpcs';
import { EVENTS, EVENT_MAP } from './tables/Events';
import { TAGS, TAG_MAP } from './tables/Tags';
import { NUMERIC_EVENTS } from './tables/NumericEvents';
import { NOTICE_PLOT_HOOKS } from './tables/NoticePlotHooks';
import { NPCS, NPC_MAP, MANAGER } from './tables/NPCs';
import { ACHIEVEMENTS, ACHIEVEMENT_MAP } from './tables/Achievements';
import { POST_TYPES, WEIBO_POST_TYPES, WEIBO_MAP } from './tables/WeiboPosts';
import { ASSISTANT } from './Assistant';
import { ATTR_SCALE, GROWTH_SCALE } from '../core/GameConfig';

/**
 * ===== 属性尺度整表预处理（批3-A；尺度扩充批 2026-09-28 拆门槛尺/成长尺）=====
 * 数据文件按 100 制书写；本模块被所有系统 import，模块体在任何人读表前执行一次，
 * 就地改（SCRIPT_MAP/EVENT_MAP 等同引用自动生效）：
 *  - 门槛侧 ×ATTR_SCALE：属性 max + startRange / requirements / 事件 dc；
 *  - 成长侧 ×GROWTH_SCALE：杀青 attributeGrowth / 事件 effects+failEffects 属性键 /
 *    行程 attributeGain（课程）——两尺解耦即「门槛抬 5/3、涨法不变」的全案本体。
 * 缩放白名单 = 8 支属性 + reputation；money/fame/energy 资源键一律不乘。
 */
const SCALED_KEYS = new Set<string>([...ATTRIBUTE_KEYS]);

function scaleAttrKeys(effects: Record<string, number> | undefined, factor: number): void {
    if (!effects) return;
    for (const k of Object.keys(effects)) {
        if (SCALED_KEYS.has(k)) effects[k] *= factor;
    }
}

(function applyAttributeScale(): void {
    for (const key of ATTRIBUTE_KEYS) {
        const def = ATTRIBUTE_DEFS[key];
        def.max *= ATTR_SCALE;
        // min 一起乘（2026-10-04 口碑可负批补）：历史上进过本表的 min 全是 0，乘不乘看不出来，
        // 直到口碑写了 -100 —— 不乘就会得到「100 制下界 -100 配 500 制上限」的错尺（负值只能到 -100）。
        // 其余七维 min=0，乘完仍是 0 ⇒ 零影响。⛔ 在本表直接写运行时值（-500）。
        def.min *= ATTR_SCALE;
        def.startRange = [def.startRange[0] * ATTR_SCALE, def.startRange[1] * ATTR_SCALE];
    }
    for (const s of SCRIPTS) {
        for (const r of s.roles) {
            for (const req of r.requirements) req.value *= ATTR_SCALE;
            scaleAttrKeys(r.attributeGrowth as Record<string, number>, GROWTH_SCALE);
        }
    }
    for (const e of EVENTS) {
        for (const c of e.choices) {
            if (c.type === 'check') c.dc *= ATTR_SCALE;
            scaleAttrKeys(c.effects, GROWTH_SCALE);
            scaleAttrKeys((c as { failEffects?: Record<string, number> }).failEffects, GROWTH_SCALE);
        }
    }
    for (const s of SCHEDULES) scaleAttrKeys(s.attributeGain as Record<string, number>, GROWTH_SCALE);
})();

export class GameData {
    static readonly ATTRIBUTE_DEFS = ATTRIBUTE_DEFS;
    static readonly ATTRIBUTE_KEYS = ATTRIBUTE_KEYS;
    static readonly STAGES = STAGES;
    static readonly SCHEDULES = SCHEDULES;
    static readonly SCHEDULE_MAP = SCHEDULE_MAP;
    static readonly SCRIPTS = SCRIPTS;
    /** 特殊剧本附表（内容本体在 SCRIPTS，以 scriptId 关联）；见 docs/特殊剧本实装设计.md */
    static readonly SPECIAL_SCRIPTS = SPECIAL_SCRIPTS;
    /** 固定剧情事件表（全局 trigger + 剧本钩子事件本体）；见 docs/特殊剧本实装设计.md §2.3。
     *  两张表合并成一个运行期表（引擎只认这一张）：TimelineEvents.ts＝全局主线时间线事件（写到点必发生，
     *  见 docs/主线剧情/固定剧情时间线.md），FixedEvents.ts＝剧本钩子事件（cast/done/airStart/rating）。 */
    /** 运行时事件表：时间线剧情 ＋ 固定剧情 ＋ 数值事件（**一套条目类型**，2026-10-22 统一） */
    static readonly FIXED_EVENTS = [...TIMELINE_EVENTS, ...FIXED_EVENTS, ...NUMERIC_EVENTS];
    /** 全局通告钩子表（通告生命周期信号 → 通用事件；判定口在 PlotSystem.onNoticeHook）；见 NoticePlotHooks.ts 表头 */
    static readonly NOTICE_PLOT_HOOKS = NOTICE_PLOT_HOOKS;
    /** 特殊 NPC 附表（社交文案在 NPCS 主表）；见 docs/特殊剧本实装设计.md §2.4 */
    static readonly SPECIAL_NPCS = SPECIAL_NPCS;
    /** 角色锚点查询（全库唯一读取口；见 SpecialNpcs.roleAnchorOf） */
    static readonly roleAnchorOf = roleAnchorOf;
    static readonly SCRIPT_MAP = SCRIPT_MAP;
    /** 品牌池（商务代言批 2026-10-30；来源=docs/品牌文案填写表.md） */
    static readonly BRANDS = BRANDS;
    static readonly BRAND_MAP = BRAND_MAP;
    /** 商务文案（丁工位导码：§四 吐槽 / §五 官宣模板 / §5.1 官宣热评，canonical=填写表） */
    static readonly WS_MOCK_LINES = WS_MOCK_LINES;
    static readonly ANNOUNCE_COPY = ANNOUNCE_COPY;
    static readonly ANNOUNCE_COMMENTS = ANNOUNCE_COMMENTS;
    static readonly EVENTS = EVENTS;
    static readonly EVENT_MAP = EVENT_MAP;
    static readonly TAGS = TAGS;
    static readonly TAG_MAP = TAG_MAP;
    /** 数值事件（2026-10-22 机制批；**表现为空**，用户逐条重写中） */
    static readonly NUMERIC_EVENTS = NUMERIC_EVENTS;
    static readonly NPCS = NPCS;
    static readonly NPC_MAP = NPC_MAP;
    static readonly MANAGER = MANAGER;
    static readonly ACHIEVEMENTS = ACHIEVEMENTS;
    static readonly ACHIEVEMENT_MAP = ACHIEVEMENT_MAP;
    static readonly POST_TYPES = POST_TYPES;
    static readonly WEIBO_POST_TYPES = WEIBO_POST_TYPES;
    static readonly WEIBO_MAP = WEIBO_MAP;
    static readonly ASSISTANT = ASSISTANT;

    /** 按角色等级获取剧本池（剧本任一角色等级 === level） */
    static getScriptsByRoleLevel(level: number): typeof SCRIPTS {
        return SCRIPTS.filter(s => s.roles.some(r => r.level === level));
    }

    /** 按角色等级范围获取剧本池（剧本任一角色等级 ∈ [min, max]） */
    static getScriptsByRoleLevelRange(min: number, max: number): typeof SCRIPTS {
        return SCRIPTS.filter(s => s.roles.some(r => r.level >= min && r.level <= max));
    }

    /** 获取随机事件池 */
    static getRandomEvents(): typeof EVENTS {
        return EVENTS;
    }

    /** 获取玩家当前可用的行程（根据咖位/等级过滤） */
    static getAvailableSchedules(rank: number): typeof SCHEDULES {
        return SCHEDULES.filter(s => {
            if (!s.requireRank) return true;
            return rank >= s.requireRank;
        });
    }

    /** 获取当前阶段的审查要求 */
    static getStageById(id: number): typeof STAGES[0] | undefined {
        return STAGES.find(s => s.id === id);
    }

    /** 根据年龄获取当前阶段 */
    static getStageByAge(age: number): typeof STAGES[0] | undefined {
        return STAGES.find(s => age >= s.startAge && age < s.endAge);
    }
}